<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { useDebounceFn } from '@vueuse/core'
import { useCustomerStore } from '@/stores/CustomersStore'
import { useCalendarStore } from '@/views/apps/calendar/useCalendarStore'
import { useModalStore } from '@/stores/ModalStore'
import { useEventStore } from '@/stores/EventsStore'
import { showSuccessToast } from '@/utils/toast'
import TablePaginationComplete from '@/components/pagination/TablePaginationComplete.vue'
import TicketCard from '@/components/TicketCard.vue'

const { t } = useI18n()
const { mobile, smAndDown } = useDisplay()

const customerStore = useCustomerStore()
const modalStore = useModalStore()
const calendarStore = useCalendarStore()
const eventStore = useEventStore()

const STATUS_ALL = 'all'
const STATUS_DEFAULT = 'isActive'
const options = customerStore.ticketsTableOptions

const SORT_TITLES = {
  'isActive:desc': 'active_first',
  'isActive:asc': 'expired_first',
  'timeout:asc': 'expires_the_quickest',
  'timeout:desc': 'expires_no_later_than',
}

// Sorting by isActive only makes sense with the "All" filter —
// within a selected status every item has the same value
const sortOptions = computed(() => Object.entries(SORT_TITLES)
  .filter(([value]) => options.filters.status === STATUS_ALL || !value.startsWith('isActive:'))
  .map(([value, key]) => ({ value, title: t(key) })))

const pagesCount = computed(() => Math.max(Math.ceil(options.total / options.perPage), 1))

const loadingStates = ref({}) // per-ticket loading, so the whole list does not flicker
const statusCounts = ref({}) // per-status counts for the filter chips

const fetchTickets = async () => {
  if (!customerStore.selectedCustomer?.salonerClientId) return

  const [orderColumn, orderDirection] = String(options.sortBy).split(':')
  const status = options.filters.status

  try {
    const response = await customerStore.getCustomerVouchers(
      customerStore.selectedCustomer.salonerClientId,
      2,
      {
        search: options.search,
        perPage: options.perPage,
        page: options.page,
        orderColumn,
        orderDirection,
        filters: status && status !== STATUS_ALL ? { [status]: 1 } : null,
      },
    )

    if (response?.meta) options.total = response.meta.total
    if (response?.data?.counts) statusCounts.value = response.data.counts
  } catch (error) {
    console.error('Error fetching tickets:', error)
  }
}

const onPageUpdate = async value => {
  options.page = value
  await fetchTickets()
}

const onPerPageUpdate = async value => {
  options.page = 1
  options.perPage = value // persisted via useStorage in the store
  await fetchTickets()
}

const onSortUpdate = async () => {
  options.page = 1
  await fetchTickets()
}

// VChipGroup returns undefined if `mandatory` ever gets removed
const onStatusFilterUpdate = async value => {
  options.filters.status = value ?? STATUS_ALL
  options.page = 1

  // the selected sorting may have just dropped off the list
  if (!sortOptions.value.some(option => option.value === options.sortBy))
    options.sortBy = options.filters.status === 'isExpired' ? 'timeout:desc' : 'timeout:asc'

  await fetchTickets() // fetchVouchers() in the vouchers table
}

// Search and the status chips are both filters - one button resets them to the default view
const canClearFilters = computed(() => options.filters.status !== STATUS_DEFAULT || Boolean(options.search))

const clearFilters = async () => {
  options.search = ''
  await onStatusFilterUpdate(STATUS_DEFAULT)
}

const debouncedTicketsSearch = useDebounceFn(() => {
  const search = options.search || ''

  if (search.length > 0 && search.length < 3) return

  // Search covers all passes, not just the currently filtered status
  if (search.length >= 3) options.filters.status = STATUS_ALL

  options.page = 1
  fetchTickets()
}, 500)

// Usage history kept per id — fetched on every expand and on explicit refresh
const usagesById = ref({})

const handleUsagesRequest = async ({ id }) => {
  loadingStates.value[id] = true
  delete usagesById.value[id]

  try {
    const usages = await customerStore.getVoucherUsageHistory(id)

    if (usages !== false && usages !== undefined)
      usagesById.value[id] = usages
  } catch (error) {
    console.error(`Failed to fetch usage history for ticket ${id}:`, error)
  } finally {
    loadingStates.value[id] = false
  }
}

const openEditDateDialog = async ticket => {
  const result = await modalStore.openModal('editDate', '', false, {
    id: ticket.id,
    date: ticket?.timeout ? new Date(ticket.timeout) : null,
    minDate: new Date(),
    endpointURL: '/voucherEntity/updateTimeout',
    title: t('changePassExpirationDate'),
    subtitle: `${ticket.identifier} / ${ticket.name}`,
  })

  if (!result?.success) return

  fetchTickets()
  showSuccessToast(result.message)

  // Keep an already-selected product in the open event in sync
  const product = eventStore.selectedProducts
    .find(p => p.isVoucher && p.voucherDetails?.identifier === ticket.identifier)

  if (product) {
    product.voucherDetails.timeout = `${result.newDate} 23:59:59`
    product.voucherExpirationDate = new Date(result.newDate)
  }
}


onMounted(() => {
  options.search = ''
  onStatusFilterUpdate(STATUS_DEFAULT)
})
</script>

<template>
  <VCard
    id="tickets-list-holder"
    class="ma-0 pa-0"
    flat
  >
    <!-- ======== TOOLBAR ======== -->
    <div class="d-flex flex-wrap ga-2 justify-end align-center mt-2 mb-4">
      <div
        class="d-flex flex-wrap ga-1 mr-auto"
        :class="smAndDown ? 'w-100':''"
      >
        <VChipGroup
          :model-value="options.filters.status"
          class="chip-group-custom w-100"
          selected-class="v-chip--selected"
          variant="outlined"
          mandatory
          center-active
          scroll-to-active
          filter
          :show-arrows="mobile"
          @update:model-value="onStatusFilterUpdate"
        >
          <VChip value="isActive">
            {{ $t('filter_chips.active') }}
            <template #append>
              <VBadge
                v-if="statusCounts.isActive > 0"
                class="ml-1 chip-count-badge"
                inline
                :content="statusCounts.isActive"
              />
            </template>
          </VChip>

          <VChip value="isUsed">
            {{ $t('filter_chips.used') }}
            <template #append>
              <VBadge
                v-if="statusCounts.isUsed > 0"
                class="ml-1 chip-count-badge"
                inline
                :content="statusCounts.isUsed"
              />
            </template>
          </VChip>

          <VChip value="isExpired">
            {{ $t('filter_chips.expired') }}
            <template #append>
              <VBadge
                v-if="statusCounts.isExpired > 0"
                class="ml-1 chip-count-badge"
                inline
                :content="statusCounts.isExpired"
              />
            </template>
          </VChip>

          <VChip value="all">
            {{ $t('filter_chips.all') }}
            <template #append>
              <VBadge
                v-if="statusCounts.all > 0"
                class="ml-1 chip-count-badge"
                inline
                :content="statusCounts.all"
              />
            </template>
          </VChip>
        </VChipGroup>
      </div>
      <VTextField
        v-model="options.search"
        density="compact"
        color="secondary"
        hide-details="auto"
        autocomplete="off"
        :class="mobile ? 'w-100':''"
        clearable
        :placeholder="$t('type_at_least_3chars')"
        prepend-inner-icon="ri-search-line"
        :style="mobile ? '':'min-width:200px; max-width:360px;'"
        :label="$t('search')"
        @update:model-value="debouncedTicketsSearch"
      />
      <!-- Actions stay one flex item, so the dividers never get left behind on wrap -->
      <div
        class="d-flex align-center ga-2"
        :class="mobile ? 'mt-2' : ''"
        :style="smAndDown ? 'flex: 1 1 auto;' : ''"
      >
        <VBtn
          variant="outlined"
          :title="$t('clear_filters')"
          :class="smAndDown ? 'flex-grow-1' : ''"
          :disabled="!canClearFilters || customerStore.isLoading"
          :color="canClearFilters ? 'primary' : 'gray'"
          @click="clearFilters"
        >
          <template #prepend>
            <VIcon
              icon="ri-filter-off-line"
              :class="smAndDown ? '' : 'mr-n2'"
            />
          </template>
          <span v-if="smAndDown">{{ $t('clear_filters') }}</span>
        </VBtn>
        <VDivider
          v-if="!mobile"
          vertical
          class="mx-1"
        />
        <VBtn
          variant="outlined"
          color="secondary"
          :title="$t('reload')"
          :disabled="customerStore.isLoading"
          @click="fetchTickets"
        >
          <VIcon icon="ri-reset-left-line" />
        </VBtn>
        <VDivider
          v-if="!mobile"
          vertical
          class="mx-1"
        />
      </div>
      <VSelect
        v-model="options.sortBy"
        :items="sortOptions"
        :label="$t('sorting')"
        density="compact"
        color="secondary"
        :class="mobile ? 'mt-2':''"
        hide-details
        :style="mobile ? 'inline-size: 200px;':'max-width:200px;'"
        :disabled="customerStore.isLoading"
        @update:model-value="onSortUpdate"
      />
    </div>

    <!-- ======== LIST ======== -->
    <div
      v-if="customerStore.isLoading"
      class="d-flex flex-column ga-3"
    >
      <TicketCard
        v-for="n in Math.max(customerStore.gatheredUserInfo?.vouchers?.passesCount, 1)"
        :key="n"
        :ticket="{}"
        is-loading
      />
    </div>

    <div
      v-else-if="customerStore.customerTickets?.length"
      class="d-flex flex-column ga-3"
    >
      <TicketCard
        v-for="ticket in customerStore.customerTickets"
        :key="ticket.id"
        :ticket="ticket"
        :usages="usagesById[ticket.id] || []"
        :usages-loading="!!loadingStates[ticket.id]"
        @usages-request="handleUsagesRequest"
        @calendar-click="usage => calendarStore.displayElementOnCalendar(usage, usage.date)"
        @edit-timeout="openEditDateDialog"
      />
    </div>

    <div
      v-else
      class="text-center text-medium-emphasis py-8"
    >
      {{ $t('no_matching_results') }}
      <!--      {{ $t('noTicketsFound') }}-->
    </div>

    <!-- ======== PAGINATION ======== -->
    <TablePaginationComplete
      v-if="options.total > 0"
      :model-value="options.page"
      :per-page="options.perPage"
      :length="pagesCount"
      :page-size-items="[5, 10, 15, 20]"
      show-results-summary
      :filtered-count="options.total"
      :total-count="options.total"
      class="mt-4"
      @update:model-value="onPageUpdate"
      @update:per-page="onPerPageUpdate"
    />
  </VCard>
</template>
